import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { Contract, Customer, Guarantor, Loan } from "@dfp/api-client";
import { AddGuarantorForm } from "../components/AddGuarantorForm.js";
import { Badge } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CustomerNotificationsPanel } from "../components/CustomerNotificationsPanel.js";
import { DocumentImage } from "../components/DocumentImage.js";
import { EditCustomerForm } from "../components/EditCustomerForm.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

export function CustomerDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const customer = useQuery({
    queryKey: ["customers", "byId", id],
    queryFn: () => api.get<Customer>(`/api/customers/${id}`),
  });
  const guarantors = useQuery({
    queryKey: ["customers", "guarantors", id],
    queryFn: () => api.get<readonly Guarantor[]>(`/api/customers/${id}/guarantors`),
  });
  // Filter the country-wide lists client-side; cheap, and the lists are
  // already cached via TanStack Query when the user has visited Loans/Contracts.
  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
  });
  const contracts = useQuery({
    queryKey: ["contracts", "list"],
    queryFn: () => api.get<readonly Contract[]>("/api/contracts"),
  });
  const customerLoans = (loans.data ?? []).filter((l) => l.customerId === id);
  const loanIds = new Set(customerLoans.map((l) => l.id));
  const customerContracts = (contracts.data ?? []).filter((c) => loanIds.has(c.loanId));

  if (customer.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (customer.isError || !customer.data) {
    return <p className="text-sm text-red-700">Failed to load customer.</p>;
  }
  const c = customer.data;
  const full = [c.givenName, c.otherNames, c.familyName].filter(Boolean).join(" ");
  const hasAddress = c.addressHouseNumber != null;
  const hasGps = c.gpsLat != null && c.gpsLng != null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{full}</h1>
        <p className="text-xs font-mono text-slate-500">{c.id}</p>
      </div>

      <EditCustomerForm customer={c} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <Badge tone={c.status === "active" ? "success" : "warning"}>{c.status}</Badge>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row k="Language" v={c.language} />
            <Row k="Risk score" v={c.riskScore != null ? String(c.riskScore) : "—"} />
            <Row k="Vulnerable" v={c.vulnerableCustomer ? "yes" : "no"} />
            <Row
              k="Registered by"
              v={
                c.registeredBy
                  ? `${c.registeredBy.displayName ?? "Unknown user"}${
                      c.registeredBy.type ? ` (${c.registeredBy.type.replace(/_/g, " ")})` : ""
                    }`
                  : "—"
              }
            />
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-xs uppercase tracking-wide text-slate-500">Verification</span>
              <Badge tone={c.verificationLevel === "physical_address" ? "success" : "neutral"}>
                {c.verificationLevel === "physical_address" ? "physical address" : "ID only"}
              </Badge>
            </div>
            {c.doNotVisitReason ? (
              <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                <strong>Do not visit:</strong> {c.doNotVisitReason}
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>KYC</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            {/* Full phone / national ID are returned only to callers holding
                customer.pii.view (server-gated); fall back to the mask. */}
            <Row k="Phone" v={c.phone ?? c.phoneMasked} />
            <Row k="National ID" v={c.nationalId ?? c.nationalIdMasked ?? "••••"} />
            <Row k="National ID hash" v={shorten(c.nationalIdLookupHash)} />
            <Row k="Phone hash" v={shorten(c.phoneLookupHash)} />
            <div className="grid grid-cols-2 gap-2 pt-2">
              <DocumentImage refKey={c.photoRef} label="Customer photo" />
              <DocumentImage refKey={c.idDocRef} label="ID document" />
            </div>
            <p className="pt-1 text-xs text-slate-500">
              Full phone and national ID are visible to roles with the{" "}
              <code>customer.pii.view</code> permission; others see the masked form.
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Address</CardTitle>
          {hasAddress ? (
            <Badge tone="success">captured</Badge>
          ) : (
            <Badge tone="neutral">not captured</Badge>
          )}
        </CardHeader>
        <CardBody className="space-y-2 text-sm">
          {!hasAddress ? (
            <p className="text-slate-500">
              No physical address captured. Verification level is{" "}
              <code className="rounded bg-slate-100 px-1">{c.verificationLevel}</code>.
            </p>
          ) : (
            <>
              <Row k="House number" v={c.addressHouseNumber ?? "—"} />
              {c.addressStreet ? <Row k="Street" v={c.addressStreet} /> : null}
              {c.addressArea ? <Row k="Area" v={c.addressArea} /> : null}
              {c.addressCity ? <Row k="City" v={c.addressCity} /> : null}
              {c.addressDistrict ? <Row k="District" v={c.addressDistrict} /> : null}
              {c.addressRegion ? <Row k="Region" v={c.addressRegion} /> : null}
              {c.addressNotes ? (
                <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
                  <strong>Notes:</strong> {c.addressNotes}
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>GPS</CardTitle>
          {hasGps ? (
            <Badge tone="success">captured</Badge>
          ) : (
            <Badge tone="neutral">not captured</Badge>
          )}
        </CardHeader>
        <CardBody className="space-y-2 text-sm">
          {!hasGps ? (
            <p className="text-slate-500">
              No GPS coordinates captured.{" "}
              {c.verificationLevel === "physical_address" ? (
                <strong className="text-red-700">
                  Required for physical-address verification — this record is inconsistent.
                </strong>
              ) : (
                "Optional for ID-only verification."
              )}
            </p>
          ) : (
            <>
              <Row k="Coordinates" v={`${c.gpsLat!.toFixed(5)}, ${c.gpsLng!.toFixed(5)}`} />
              <Row
                k="Accuracy"
                v={c.gpsAccuracyMetres != null ? `±${c.gpsAccuracyMetres}m` : "—"}
              />
              <a
                className="mt-2 inline-block text-xs text-brand-600 hover:underline"
                href={`https://www.google.com/maps?q=${c.gpsLat},${c.gpsLng}`}
                target="_blank"
                rel="noreferrer"
              >
                Open in maps ↗
              </a>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Additional photos</CardTitle>
          <span className="text-xs text-slate-500">{c.additionalPhotoRefs.length}</span>
        </CardHeader>
        <CardBody>
          {c.additionalPhotoRefs.length === 0 ? (
            <p className="text-sm text-slate-500">No additional photos.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {c.additionalPhotoRefs.map((ref, i) => (
                <DocumentImage key={ref} refKey={ref} label={`#${i + 1}`} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Guarantors</CardTitle>
          <span className="text-xs text-slate-500">{guarantors.data?.length ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {guarantors.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : guarantors.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load guarantors.</p>
          ) : (guarantors.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No guarantors captured.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Relationship</Th>
                </tr>
              </THead>
              <TBody>
                {(guarantors.data ?? []).map((g) => (
                  <Tr key={g.id}>
                    <Td>
                      {g.givenName} {g.familyName}
                    </Td>
                    <Td className="font-mono text-xs">{g.phoneMasked}</Td>
                    <Td>{g.relationship ?? "—"}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
          <AddGuarantorForm customerId={id} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Loans</CardTitle>
          <span className="text-xs text-slate-500">{customerLoans.length}</span>
        </CardHeader>
        <CardBody className="p-0">
          {loans.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : loans.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load loans.</p>
          ) : customerLoans.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No loans for this customer.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>Status</Th>
                  <Th>Principal</Th>
                  <Th>Deposit</Th>
                  <Th>Terms</Th>
                </tr>
              </THead>
              <TBody>
                {customerLoans.map((l) => (
                  <Tr key={l.id}>
                    <Td>
                      <Link
                        to="/loans/$id"
                        params={{ id: l.id }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {l.id.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td>
                      <Badge tone={l.status === "ACTIVE" ? "success" : "neutral"}>{l.status}</Badge>
                    </Td>
                    <Td>{formatMoney(l.principalAmountMinor, l.principalCurrency)}</Td>
                    <Td>{formatMoney(l.depositAmountMinor, l.depositCurrency)}</Td>
                    <Td className="text-xs text-slate-600">
                      {l.repaymentFrequency ?? "—"}
                      {l.tenorMonths != null ? ` · ${l.tenorMonths}mo` : ""}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contracts</CardTitle>
          <span className="text-xs text-slate-500">{customerContracts.length}</span>
        </CardHeader>
        <CardBody className="p-0">
          {contracts.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : contracts.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load contracts.</p>
          ) : customerContracts.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No contracts for this customer.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>Signed by</Th>
                  <Th>Version</Th>
                  <Th>Signed at</Th>
                </tr>
              </THead>
              <TBody>
                {customerContracts.map((k) => (
                  <Tr key={k.id}>
                    <Td>
                      <Link
                        to="/contracts/$id"
                        params={{ id: k.id }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {k.id.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td>{k.signedByCustomer}</Td>
                    <Td>v{k.version}</Td>
                    <Td className="text-xs text-slate-600">{k.signedAt.slice(0, 19)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <CustomerNotificationsPanel customerId={id} />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900 truncate">{v}</span>
    </div>
  );
}

function shorten(s: string): string {
  return s.length > 24 ? `${s.slice(0, 12)}…${s.slice(-8)}` : s;
}
